import moment from 'moment';
import { TTransactionHistory } from '../types/TransactionHistory';
import { getTransactionStatus } from '../utils/getTransactionStatus';
import { getTeamMember } from '../utils/getTeamMember';
import {
  IBankTransaction,
  IEmergePayTransaction,
  ITransferTransaction
} from '@/services/types/TransactionData';
import { isCredit } from '../utils/isCredit';
import {
  getBillingAmount,
  getFeeResponsibilityDisplay,
  getPaymentAmount
} from '../utils/getEmergePayAmounts';
import { AccountType, AccountTypeEnum } from '@/context/types/Account';
import { TTransactionGeneralLedgerCode } from '@/context/types/Business';

const BOTH_ACCOUNT_TYPES = [AccountTypeEnum.BUSINESS, AccountTypeEnum.PERSONAL];

export const getPurchaseDetails = (accountType: AccountType, transaction: TTransactionHistory) => {
  return extractRowsByAccountType(
    [
      {
        title: 'Time',
        data: moment(transaction.data.date).format('LLLL'),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Status',
        data: getTransactionStatus(transaction.data.state),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'GL code',
        data: transaction.details?.generalLedgerCode,
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Team member',
        data: getTeamMember(transaction),
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Card',
        data: transaction.card?.panEnd,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Account name',
        data: transaction.wallet?.name,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Account number',
        data: transaction.wallet?.accountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Balance after transaction',
        data: transaction.data.balance,
        accountTypes: BOTH_ACCOUNT_TYPES
      }
    ],
    accountType
  );
};

export const getBankTransferOutgoingDetails = (
  accountType: AccountType,
  transaction: TTransactionHistory
) => {
  const outgoingBankTransfer = transaction.data as IBankTransaction;

  return extractRowsByAccountType(
    [
      {
        title: 'Time',
        data: moment(outgoingBankTransfer.date).format('LLLL'),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Status',
        data: getTransactionStatus(outgoingBankTransfer.state),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'GL code',
        data: transaction.details?.generalLedgerCode,
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Team member',
        data: getTeamMember(transaction),
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Account name',
        data: transaction.wallet?.name,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Account number',
        data: transaction.wallet?.accountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Paid to Account name',
        data: outgoingBankTransfer.externalName,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Paid to Account number',
        data: outgoingBankTransfer.externalAccountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Balance after transaction',
        data: transaction.data.balance,
        accountTypes: BOTH_ACCOUNT_TYPES
      }
    ],
    accountType
  );
};

export const getBankTransferIncomingDetails = (
  accountType: AccountType,
  transaction: TTransactionHistory
) => {
  const incomingBankTransfer = transaction.data as IBankTransaction;

  return extractRowsByAccountType(
    [
      {
        title: 'Time',
        data: moment(incomingBankTransfer.date).format('LLLL'),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Status',
        data: getTransactionStatus(incomingBankTransfer.state),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'GL code',
        data: transaction.details?.generalLedgerCode,
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Account name',
        data: transaction.wallet?.name,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Account number',
        data: transaction.wallet?.accountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Paid from Account name',
        data: incomingBankTransfer.externalName,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Paid from Account number',
        data: incomingBankTransfer.externalAccountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Balance after transaction',
        data: transaction.data.balance,
        accountTypes: BOTH_ACCOUNT_TYPES
      }
    ],
    accountType
  );
};

export const getAccountTransferDetails = (
  accountType: AccountType,
  transaction: TTransactionHistory
) => {
  const transferTransaction = transaction.data as ITransferTransaction;

  return extractRowsByAccountType(
    [
      {
        title: 'Time',
        data: moment(transaction.data.date).format('LLLL'),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Status',
        data: getTransactionStatus(transaction.data.state),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'GL code',
        data: transaction.details?.generalLedgerCode,
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Team member',
        data: getTeamMember(transaction),
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: isCredit(transferTransaction.sign) ? 'To account name' : 'From account name',
        data: isCredit(transferTransaction.sign)
          ? transaction.toWallet?.name
          : transaction.wallet?.name,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: isCredit(transferTransaction.sign) ? 'To account number' : 'From account number',
        data: isCredit(transferTransaction.sign)
          ? transaction.toWallet?.accountNumber
          : transaction.wallet?.accountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Balance after transaction',
        data: transaction.data.balance,
        accountTypes: BOTH_ACCOUNT_TYPES
      }
    ],
    accountType
  );
};

export const getAdminFeesDetails = (accountType: AccountType, transaction: TTransactionHistory) => {
  return extractRowsByAccountType(
    [
      {
        title: 'Time',
        data: moment(transaction.data.date).format('LLLL'),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Status',
        data: getTransactionStatus(transaction.data.state),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'GL code',
        data: transaction.details?.generalLedgerCode,
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Account name',
        data: transaction.wallet?.name,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Account number',
        data: transaction.wallet?.accountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Balance after transaction',
        data: transaction.data.balance,
        accountTypes: BOTH_ACCOUNT_TYPES
      }
    ],
    accountType
  );
};

export const getTopUpDetails = (accountType: AccountType, transaction: TTransactionHistory) => {
  return extractRowsByAccountType(
    [
      {
        title: 'Time',
        data: moment(transaction.data.date).format('LLLL'),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Status',
        data: getTransactionStatus(transaction.data.state),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'GL code',
        data: transaction.details?.generalLedgerCode,
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Team member',
        data: getTeamMember(transaction),
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Account name',
        data: transaction.wallet?.name,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Account number',
        data: transaction.wallet?.accountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Balance after transaction',
        data: transaction.data.balance,
        accountTypes: BOTH_ACCOUNT_TYPES
      }
    ],
    accountType
  );
};

export const getEmergePayDetails = (accountType: AccountType, transaction: TTransactionHistory) => {
  const transactionData = transaction.data as IEmergePayTransaction;
  return extractRowsByAccountType(
    [
      {
        title: 'Time',
        data: moment(transactionData.date).format('LLLL'),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Status',
        data: getTransactionStatus(transactionData.state),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'GL code',
        data: transaction.details?.generalLedgerCode,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Transaction ID',
        data: transactionData.txnId,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Billing amount',
        data: getBillingAmount(transactionData),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Payment amount',
        data: getPaymentAmount(transactionData),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: '1.5% Fee responsibility',
        data: getFeeResponsibilityDisplay(transactionData),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Team member',
        data: getTeamMember(transaction),
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Account name',
        data: transaction.wallet?.name,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Account number',
        data: transaction.wallet?.accountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Balance after transaction',
        data: transaction.data.balance,
        accountTypes: BOTH_ACCOUNT_TYPES
      }
    ],
    accountType
  );
};

export const getInterestDetails = (accountType: AccountType, transaction: TTransactionHistory) => {
  return extractRowsByAccountType(
    [
      {
        title: 'Time',
        data: moment(transaction.data.date).format('LLLL'),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Status',
        data: getTransactionStatus(transaction.data.state),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'GL code',
        data: transaction.details?.generalLedgerCode,
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Account name',
        data: transaction.wallet?.name,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Account number',
        data: transaction.wallet?.accountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Balance after transaction',
        data: transaction.data.balance,
        accountTypes: BOTH_ACCOUNT_TYPES
      }
    ],
    accountType
  );
};

export const getTaxDetails = (accountType: AccountType, transaction: TTransactionHistory) => {
  return extractRowsByAccountType(
    [
      {
        title: 'Time',
        data: moment(transaction.data.date).format('LLLL'),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Status',
        data: getTransactionStatus(transaction.data.state),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'GL code',
        data: transaction.details?.generalLedgerCode,
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Account name',
        data: transaction.wallet?.name,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Account number',
        data: transaction.wallet?.accountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Balance after transaction',
        data: transaction.data.balance,
        accountTypes: BOTH_ACCOUNT_TYPES
      }
    ],
    accountType
  );
};

export const getInvestmentDetails = (
  accountType: AccountType,
  transaction: TTransactionHistory
) => {
  const transferTransaction = transaction.data as ITransferTransaction;

  return extractRowsByAccountType(
    [
      {
        title: 'Time',
        data: moment(transaction.data.date).format('LLLL'),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Status',
        data: getTransactionStatus(transaction.data.state),
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'GL code',
        data: transaction.details?.generalLedgerCode,
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: 'Team member',
        data: getTeamMember(transaction),
        accountTypes: [AccountTypeEnum.BUSINESS]
      },
      {
        title: isCredit(transferTransaction.sign) ? 'To account name' : 'From account name',
        data: isCredit(transferTransaction.sign)
          ? transaction.toWallet?.name
          : transaction.wallet?.name,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: isCredit(transferTransaction.sign) ? 'To account number' : 'From account number',
        data: isCredit(transferTransaction.sign)
          ? transaction.toWallet?.accountNumber
          : transaction.wallet?.accountNumber,
        accountTypes: BOTH_ACCOUNT_TYPES
      },
      {
        title: 'Balance after transaction',
        data: transaction.data.balance,
        accountTypes: BOTH_ACCOUNT_TYPES
      }
    ],
    accountType
  );
};

type RowData = {
  title: string;
  data:
    | string
    | number
    | ReturnType<typeof getTeamMember>
    | TTransactionGeneralLedgerCode
    | undefined
    | null;
  accountTypes: AccountType[];
};

const extractRowsByAccountType = (rows: RowData[], accountType: AccountType) =>
  rows.filter((row) => row.accountTypes.some((type) => type === accountType));
